import React from "react";
import { Box, Typography, Button } from "@mui/material";
import SearchableMultiSelect from "./SearchableMultiSelect";

/**
 * Supply-type allocation dashboard filter bar.
 * All dropdowns use the shared SearchableMultiSelect component
 * (same design as SeriesDashboard filters).
 *
 * The parent handlers (handleProjectChange, etc.) expect a synthetic
 * event with { target: { value } }, so we wrap the SearchableMultiSelect
 * onChange to match that contract.
 */
const wrap = (handler) => (newArr) =>
	handler({ target: { value: newArr } });

const FilterSection = ({
	tempSelectedProject,
	tempSelectedProgramm,
	tempSelectedPurchaseType,
	tempSelectedTeams,
	tempSelectedCategory,
	handleProjectChange,
	handleProgrammChange,
	handlePurchaseTypeChange,
	handleTeamChange,
	handleCategoryChange,
	handleApplyFilters,
	handleResetFilters,
	hasFiltersChanged,
	filterOptions,
	filteredSeTeams,
}) => {
	// Derive non-"all" selections to pass into SearchableMultiSelect value
	const activeProjects = (tempSelectedProject || []).filter((v) => v !== "all");
	const activeProgramms = (tempSelectedProgramm || []).filter((v) => v !== "all");
	const activePurchaseTypes = (tempSelectedPurchaseType || []).filter((v) => v !== "all");
	const activeTeams = (tempSelectedTeams || []).filter((v) => v !== "all");
	const activeCategories = (tempSelectedCategory || []).filter((v) => v !== "all");

	// Wrap: when SearchableMultiSelect calls onChange([...selections]),
	// convert back to the parent handler's expected event format.
	// An empty array means "all" → pass ["all"] so parent logic stays intact.
	const projectChange = (arr) =>
		handleProjectChange({ target: { value: arr.length === 0 ? ["all"] : arr } });
	const programmChange = (arr) =>
		handleProgrammChange({ target: { value: arr.length === 0 ? ["all"] : arr } });
	const purchaseTypeChange = (arr) =>
		handlePurchaseTypeChange({ target: { value: arr.length === 0 ? ["all"] : arr } });
	const teamChange = (arr) =>
		handleTeamChange({ target: { value: arr.length === 0 ? ["all"] : arr } });
	const categoryChange = (arr) =>
		handleCategoryChange({ target: { value: arr.length === 0 ? ["all"] : arr } });

	return (
		<Box
			data-tour-target="sta-dash-filters"
			sx={{
				display: "flex",
				gap: 1,
				alignItems: "center",
				flexWrap: "wrap",
				justifyContent: "flex-end",
				backgroundColor: "#f8f9fa",
				padding: "12px 16px",
				borderRadius: "8px",
				border: "1px solid #e0e0e0",
			}}
		>
			<Typography
				variant="body2"
				sx={{
					fontFamily: "MAN EUROPE_Bold",
					color: "#1e2832",
					fontSize: "0.85rem",
					minWidth: "50px",
					flexShrink: 0,
					marginRight: "8px",
				}}
			>
				Filters:
			</Typography>

			<SearchableMultiSelect
				label="Projects"
				options={filterOptions?.projects || []}
				value={activeProjects}
				onChange={projectChange}
				minWidth={110}
			/>

			<SearchableMultiSelect
				label="Programs"
				options={filterOptions?.programms || []}
				value={activeProgramms}
				onChange={programmChange}
				minWidth={110}
			/>

			<SearchableMultiSelect
				label="Purchase Types"
				options={filterOptions?.purchaseTypes || []}
				value={activePurchaseTypes}
				onChange={purchaseTypeChange}
				minWidth={130}
			/>

			<SearchableMultiSelect
				label="Teams"
				options={filteredSeTeams || []}
				value={activeTeams}
				onChange={teamChange}
				minWidth={90}
				displayFn={(t) => (t === "-" ? "SET Allocation Pending" : t)}
			/>

			<SearchableMultiSelect
				label="Category"
				options={filterOptions?.categories || []}
				value={activeCategories}
				onChange={categoryChange}
				minWidth={110}
			/>

			{/* Action Buttons */}
			<Button
				size="small"
				variant="outlined"
				onClick={handleResetFilters}
				sx={{
					fontFamily: "MAN EUROPE_Bold",
					fontSize: "0.75rem",
					borderColor: "#666",
					color: "#666",
					minWidth: "60px",
					height: "32px",
					padding: "6px 12px",
					flexShrink: 0,
					borderRadius: "6px",
					textTransform: "none",
					"&:hover": {
						borderColor: "#444",
						backgroundColor: "rgba(102, 102, 102, 0.04)",
					},
				}}
			>
				Reset
			</Button>

			<Button
				size="small"
				variant="contained"
				onClick={handleApplyFilters}
				disabled={!hasFiltersChanged}
				sx={{
					fontFamily: "MAN EUROPE_Bold",
					fontSize: "0.75rem",
					backgroundColor: hasFiltersChanged ? "#cc0000" : "#ccc",
					color: "white",
					minWidth: "60px",
					height: "32px",
					padding: "6px 12px",
					flexShrink: 0,
					borderRadius: "6px",
					textTransform: "none",
					boxShadow: hasFiltersChanged ? "0 2px 4px rgba(204, 0, 0, 0.2)" : "none",
					"&:hover": {
						backgroundColor: hasFiltersChanged ? "#aa0000" : "#bbb",
						boxShadow: hasFiltersChanged ? "0 2px 6px rgba(204, 0, 0, 0.3)" : "none",
					},
					"&:disabled": {
						backgroundColor: "#ccc",
						color: "#666",
					},
				}}
			>
				Apply
			</Button>
		</Box>
	);
};

export default FilterSection;
